import { Injectable, Renderer2 } from '@angular/core';

import type { TooltipPosition, TooltipSeverity } from '@shared-models';

@Injectable({
  providedIn: 'root',
})
export class TooltipService {
  // Animation timing constants
  private readonly TOOLTIP_SHOW_DELAY_MS = 10; // Small delay to ensure CSS classes are applied before animation
  private readonly TOOLTIP_HIDE_DELAY_MS = 200; // Wait for CSS transition to complete before removing from DOM

  // Positioning constants
  private readonly VIEWPORT_MARGIN_PX = 10; // Minimum distance from viewport edges
  private readonly TOOLTIP_GAP_PX = 12; // Space between tooltip and host element
  private readonly ARROW_EDGE_INSET_PX = 14; // Arrow half-width + corner radius
  /**
   * Creates and displays a tooltip
   */
  createTooltip(config: {
    hostElement: HTMLElement;
    message: string;
    position: TooltipPosition;
    severity: TooltipSeverity;
    renderer: Renderer2;
    gap?: number;
  }): HTMLElement {
    // Create tooltip element
    const tooltipElement = config.renderer.createElement('div');
    config.renderer.addClass(tooltipElement, 'v-one-tooltip');
    config.renderer.addClass(
      tooltipElement,
      `v-one-tooltip--${config.position}`,
    );
    config.renderer.addClass(
      tooltipElement,
      `v-one-tooltip--${config.severity}`,
    );

    // Create content
    const content = config.renderer.createElement('div');
    config.renderer.addClass(content, 'v-one-tooltip__content');
    config.renderer.setProperty(content, 'textContent', config.message);

    // Create arrow
    const arrow = config.renderer.createElement('div');
    config.renderer.addClass(arrow, 'v-one-tooltip__arrow');

    // Assemble tooltip
    config.renderer.appendChild(tooltipElement, content);
    config.renderer.appendChild(tooltipElement, arrow);
    config.renderer.appendChild(document.body, tooltipElement);

    // Position tooltip
    this.positionTooltip(
      tooltipElement,
      config.hostElement,
      config.position,
      config.renderer,
      config.gap,
    );

    // Show tooltip with animation
    setTimeout(() => {
      config.renderer.addClass(tooltipElement, 'v-one-tooltip--visible');
    }, this.TOOLTIP_SHOW_DELAY_MS);

    return tooltipElement;
  }

  /**
   * Hides and removes a tooltip
   */
  hideTooltip(tooltipElement: HTMLElement, renderer: Renderer2): void {
    if (!tooltipElement) {
      return;
    }

    renderer.removeClass(tooltipElement, 'v-one-tooltip--visible');

    setTimeout(() => {
      if (tooltipElement && tooltipElement.parentNode) {
        renderer.removeChild(document.body, tooltipElement);
      }
    }, this.TOOLTIP_HIDE_DELAY_MS);
  }

  /**
   * Positions tooltip relative to host element with viewport boundary checks
   */
  private positionTooltip(
    tooltipElement: HTMLElement,
    hostElement: HTMLElement,
    position: TooltipPosition,
    renderer: Renderer2,
    gap: number = this.TOOLTIP_GAP_PX,
  ): void {
    const hostRect = hostElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = hostRect.top + scrollTop - tooltipRect.height - gap;
        left =
          hostRect.left +
          scrollLeft +
          hostRect.width / 2 -
          tooltipRect.width / 2;
        break;

      case 'bottom':
        top = hostRect.bottom + scrollTop + gap;
        left =
          hostRect.left +
          scrollLeft +
          hostRect.width / 2 -
          tooltipRect.width / 2;
        break;

      case 'left':
        top =
          hostRect.top +
          scrollTop +
          hostRect.height / 2 -
          tooltipRect.height / 2;
        left = hostRect.left + scrollLeft - tooltipRect.width - gap;
        break;

      case 'right':
        top =
          hostRect.top +
          scrollTop +
          hostRect.height / 2 -
          tooltipRect.height / 2;
        left = hostRect.right + scrollLeft + gap;
        break;
    }

    // Viewport boundary adjustments
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    if (left < this.VIEWPORT_MARGIN_PX) {
      left = this.VIEWPORT_MARGIN_PX;
    } else if (
      left + tooltipRect.width >
      viewport.width - this.VIEWPORT_MARGIN_PX
    ) {
      left = viewport.width - tooltipRect.width - this.VIEWPORT_MARGIN_PX;
    }

    if (top < this.VIEWPORT_MARGIN_PX) {
      top = this.VIEWPORT_MARGIN_PX;
    } else if (
      top + tooltipRect.height >
      viewport.height - this.VIEWPORT_MARGIN_PX
    ) {
      top = viewport.height - tooltipRect.height - this.VIEWPORT_MARGIN_PX;
    }

    // Apply position
    renderer.setStyle(tooltipElement, 'position', 'absolute');
    renderer.setStyle(tooltipElement, 'top', `${Math.round(top)}px`);
    renderer.setStyle(tooltipElement, 'left', `${Math.round(left)}px`);
    renderer.setStyle(tooltipElement, 'z-index', '10000');

    this.alignArrow(
      tooltipElement,
      hostRect,
      tooltipRect,
      position,
      Math.round(top) - scrollTop,
      Math.round(left) - scrollLeft,
      renderer,
    );
  }

  private alignArrow(
    tooltipElement: HTMLElement,
    hostRect: DOMRect,
    tooltipRect: DOMRect,
    position: TooltipPosition,
    viewportTop: number,
    viewportLeft: number,
    renderer: Renderer2,
  ): void {
    const arrow = tooltipElement.querySelector('.v-one-tooltip__arrow');
    if (!arrow) {
      return;
    }

    const isVertical = position === 'top' || position === 'bottom';
    const size = isVertical ? tooltipRect.width : tooltipRect.height;
    const offset = isVertical
      ? hostRect.left + hostRect.width / 2 - viewportLeft
      : hostRect.top + hostRect.height / 2 - viewportTop;
    const clamped = Math.min(
      Math.max(offset, this.ARROW_EDGE_INSET_PX),
      size - this.ARROW_EDGE_INSET_PX,
    );

    renderer.setStyle(
      arrow,
      isVertical ? 'left' : 'top',
      `${Math.round(clamped)}px`,
    );
  }

  /**
   * Adds scroll listeners to hide tooltip when user scrolls
   */
  addScrollListener(
    hostElement: HTMLElement,
    hideCallback: () => void,
  ): () => void {
    const scrollListener = () => {
      hideCallback();
    };

    // Window scroll listener
    window.addEventListener('scroll', scrollListener, { passive: true });

    // Parent containers with scroll
    let parent = hostElement.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      if (
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll'
      ) {
        parent.addEventListener('scroll', scrollListener, { passive: true });
      }
      parent = parent.parentElement;
    }

    // Return cleanup function
    return () => {
      window.removeEventListener('scroll', scrollListener);

      let parent = hostElement.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (
          style.overflow === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll'
        ) {
          parent.removeEventListener('scroll', scrollListener);
        }
        parent = parent.parentElement;
      }
    };
  }

  /**
   * Detects if device supports real hover (not touch devices)
   */
  isDesktop(): boolean {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }
}
